<script setup>
import {
  computed,
  defineEmits,
  defineProps,
  reactive,
  ref,
  unref,
  watch,
} from "vue";
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useVueTable,
  FlexRender,
} from "@tanstack/vue-table";
import { Dialog, DialogPanel } from "@headlessui/vue";
import BaseButton from "@/components/shared/Button/BaseButton.vue";
import Pagination from "@/components/shared/Pagination.vue";
import { columnsConfig } from "@/components/program-manager/sessions/components/Academics/constants";
import Spinner from "@/components/helpers/Spinner.vue";
import CloseIcon from "@/components/svg-icons/CloseIcon.vue";
import CheckboxSelect from "@/components/shared/select/CheckboxSelect.vue";
import {
  useGetClasses,
  useGetHostInstitutionOptions,
  debounceSearchWrapper,
  useGetRequiredMajorOptions,
} from "@/components/program-manager/sessions/composable";
import Magnifier from "@/components/shared/icons/Magnifier";
import {
  learnerTypesOptions,
  proficiencyLevelsOptions,
  requiredLanguagesOptions,
} from "@/components/program-manager/sessions/constants";
import { cloneDeep } from "lodash";

import BadgeBar from "@/components/program-manager/sessions/components/BadgeBar";
import { convertClassFilterCriteria } from "@/components/program-manager/sessions/utils";
import { useStore } from "vuex";
import { helpers, required } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import ErrorMessage from "@/components/shared/ErrorMessage.vue";

const props = defineProps({
  terms: {
    type: Array,
    required: false,
    default: () => [],
  },
  hostInstitution: {
    type: Object,
    required: false,
  },
  open: {
    type: Boolean,
    required: false,
    default: false,
  },
  originalFilters: {
    type: Object,
    required: false,
  },
});

const emit = defineEmits(["closeModal", "openModal", "updateFilters"]);

const store = useStore();
const getTermsData = computed(
  () => store.getters["programManager/getTermsData"]
);

const isModalOpen = ref(props.open);
const openModal = () => {
  isModalOpen.value = true;
  fetchClassesData(0, { ...classesParams.value, limit: 10, skip: 0 });
  emit("openModal", true);
};
const closeModal = () => {
  isModalOpen.value = false;
  emit("closeModal", true);
};

const columnHelper = createColumnHelper();
const columns = columnsConfig.map(
  ({ header, id, size, getColumnDisplayValue }) => {
    return columnHelper.accessor(getColumnDisplayValue, {
      id,
      header,
      size,
    });
  }
);

const filters = ref({
  host_institutions: props?.originalFilters
    ? cloneDeep(props?.originalFilters?.host_institutions || [])
    : [cloneDeep(props.hostInstitution)].filter((val) => !!val),
  cip_codes: cloneDeep(props?.originalFilters?.cip_codes || []),
  required_languages: cloneDeep(
    props?.originalFilters?.required_languages || []
  ),
  proficiency_levels: cloneDeep(
    props?.originalFilters?.proficiency_levels || []
  ),
  education_levels: cloneDeep(props?.originalFilters?.education_levels || []),
  class_format: cloneDeep(props?.originalFilters?.class_format),
  terms: props?.originalFilters
    ? cloneDeep(props?.originalFilters?.terms)
    : cloneDeep(props.terms),
});

const rules = {
  host_institutions: {
    minlength: helpers.withMessage(
      "Choose at least one host institution.",
      required
    ),
  },
  terms: {
    minlength: helpers.withMessage("Choose at least one term", required),
  },
};

const v$ = useVuelidate(rules, filters);

watch(
  () => props.originalFilters,
  (updatedFilters) => {
    if (updatedFilters) {
      filters.value.host_institutions = cloneDeep(
        updatedFilters.host_institutions
      );
      filters.value.cip_codes = cloneDeep(updatedFilters.cip_codes);
      filters.value.required_languages = cloneDeep(
        updatedFilters.required_languages
      );
      filters.value.proficiency_levels = cloneDeep(
        updatedFilters.proficiency_levels
      );
      filters.value.education_levels = cloneDeep(
        updatedFilters.education_levels
      );
      filters.value.class_format = cloneDeep(updatedFilters.class_format);
    } else {
      resetFilters();
    }
  },
  { deep: true }
);

const pagination = reactive({
  pageIndex: 1,
  pageSize: 10,
});

const classesParams = ref(convertClassFilterCriteria(filters));

const {
  isLoading: classesDataLoading,
  execute: fetchClassesData,
  state: classesData,
  isReady: classesIsReady,
} = useGetClasses(
  { immediate: false, throwError: false, resetOnExecute: false },
  { ...classesParams.value, limit: 10, skip: 0 }
);

watch(
  [filters],
  ([newFilters]) => {
    classesParams.value = convertClassFilterCriteria(newFilters);
    if (isModalOpen.value) {
      fetchClassesData(0, {
        ...classesParams.value,
        limit: pagination.pageSize,
        skip: pagination.pageSize * (pagination.pageIndex - 1),
        cancel: true,
      });
    }
  },
  { deep: true, immediate: true }
);

const table = useVueTable({
  get data() {
    return classesData?.value?.data || [];
  },
  columns: columns,
  columnResizeMode: "onChange",
  getCoreRowModel: getCoreRowModel(),
  getSortedRowModel: getSortedRowModel(),
  manualPagination: true,
});

const totalRecords = computed(() =>
  classesData?.value?.count && classesData?.value?.count !== -1
    ? classesData.value.count
    : 0
);

watch(
  () => props.open,
  (newValue) => {
    if (newValue === true) {
      openModal();
    }
  }
);

const changePage = (pageIndex) => {
  pagination.pageIndex = pageIndex;
  fetchClassesData(0, {
    ...classesParams.value,
    limit: pagination.pageSize,
    skip: pagination.pageSize * (pagination.pageIndex - 1),
    cancel: true,
  });
};

const resetFilters = () => {
  filters.value.host_institutions = props.hostInstitution
    ? [cloneDeep(props.hostInstitution)]
    : [];
  filters.value.cip_codes = [];
  filters.value.required_languages = [];
  filters.value.proficiency_levels = [];
  filters.value.education_levels = [];
  filters.value.class_format = undefined;
  filters.value.terms = props.terms ? cloneDeep(props.terms) : [];
};
const onCancel = () => {
  isModalOpen.value = false;
  emit("closeModal", true);
};
const addCriteria = async () => {
  v$.value.$reset();
  const isValid = await v$.value.$validate();
  if (isValid) {
    isModalOpen.value = false;
    emit("closeModal", true);
    emit("updateFilters", cloneDeep(unref(filters)));
  }
};

const {
  execute: executeFetchHostInstitutionOptions,
  state: hostInstitutionOptions,
  isLoading: hostInstitutionOptionsLoading,
  isReady: hostInstitutionIsReady,
} = useGetHostInstitutionOptions();

const fetchHostInstitutionOptions = debounceSearchWrapper(
  executeFetchHostInstitutionOptions,
  250
);

const {
  execute: executeFetchRequiredMajorOptions,
  state: requiredMajorOptions,
  isLoading: requiredMajorOptionsLoading,
  isReady: requiredMajorOptionsIsReady,
} = useGetRequiredMajorOptions();

const fetchRequiredMajorOptions = debounceSearchWrapper(
  executeFetchRequiredMajorOptions,
  250
);
</script>

<template>
  <div>
    <Dialog
      :open="isModalOpen"
      class="relative z-50"
      @closeModal="closeModal"
      @click.self="closeModal"
    >
      <div class="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel>
          <div
            class="fixed inset-0 z-0 hidden bg-university-primary opacity-50 sm:block"
          ></div>
          <div class="bg-white h-[95vh] p-6 rounded-xl z-20 relative w-[95vw]">
            <div class="text-right">
              <button @click="closeModal">
                <CloseIcon size="28" stroke="#007f80" stroke-width="1.75" />
              </button>
            </div>
            <section class="flex flex-col h-[85vh]">
              <h1
                class="flex justify-center text-xl leading-tight text-center sm:text-2xl md:text-3xl mb-6 flex-0"
              >
                Add Classes Criteria
              </h1>
              <div
                class="flex bg-blue-450 rounded-md ml-2 px-2 flex-wrap flex-shrink"
              >
                <CheckboxSelect
                  v-model="filters.host_institutions"
                  outer-classes="w-52 mx-2 my-2 checkbox-placeholder"
                  label="name"
                  :multiple="true"
                  :close-on-select="false"
                  placeholder="Host Institutions"
                  :components="{ OpenIndicator: Magnifier }"
                  :options="hostInstitutionOptions?.items || []"
                  :loading="
                    hostInstitutionOptionsLoading || !hostInstitutionIsReady
                  "
                  :reduce="(val) => ({ id: val.id, name: val.name })"
                  :deselect-from-dropdown="true"
                  :clear-search-on-blur="() => false"
                  option-classes="block text-xs text-gray-700 capitalize whitespace-pre-line"
                  scroll-input="auto"
                  input-max-height="29px"
                  @search="fetchHostInstitutionOptions"
                />
                <CheckboxSelect
                  v-model="filters.terms"
                  :clear-search-on-blur="() => false"
                  :close-on-select="false"
                  :components="{ OpenIndicator: Magnifier }"
                  :deselect-from-dropdown="true"
                  label="name"
                  :multiple="true"
                  :options="getTermsData"
                  outer-classes="w-52 mx-2 my-2 checkbox-placeholder"
                  placeholder="Term"
                  scroll-input="auto"
                  input-max-height="29px"
                />
                <CheckboxSelect
                  v-model="filters.cip_codes"
                  outer-classes="w-52 mx-2 my-2 checkbox-placeholder"
                  label="name"
                  :multiple="true"
                  :close-on-select="false"
                  placeholder="Area of Study"
                  :components="{ OpenIndicator: Magnifier }"
                  :options="requiredMajorOptions"
                  :loading="
                    requiredMajorOptionsLoading || !requiredMajorOptionsIsReady
                  "
                  :deselect-from-dropdown="true"
                  :clear-search-on-blur="() => false"
                  option-classes="block text-sm text-gray-700 capitalize whitespace-pre-line"
                  scroll-input="auto"
                  input-max-height="29px"
                  @search="fetchRequiredMajorOptions"
                />
                <CheckboxSelect
                  v-model="filters.required_languages"
                  :options="requiredLanguagesOptions"
                  outer-classes="w-52 mx-2 my-2 checkbox-placeholder"
                  :multiple="true"
                  :deselect-from-dropdown="true"
                  :close-on-select="false"
                  placeholder="Language(s) of Instruction"
                  label="value"
                  scroll-input="auto"
                  input-max-height="29px"
                />
                <CheckboxSelect
                  v-model="filters.proficiency_levels"
                  :options="proficiencyLevelsOptions"
                  outer-classes="w-52 mx-2 my-2 checkbox-placeholder"
                  :multiple="true"
                  :deselect-from-dropdown="true"
                  :close-on-select="false"
                  placeholder="Language Level(s)"
                  label="value"
                  scroll-input="auto"
                  input-max-height="29px"
                />
                <CheckboxSelect
                  v-model="filters.education_levels"
                  :options="learnerTypesOptions"
                  outer-classes="w-52 mx-2 my-2 checkbox-placeholder"
                  :multiple="true"
                  :deselect-from-dropdown="true"
                  :close-on-select="false"
                  placeholder="Eductaion Level"
                  label="value"
                  scroll-input="auto"
                  input-max-height="29px"
                />
              </div>
              <div class="grid grid-cols-12 flex-0">
                <BadgeBar v-model="filters" class="col-span-11" />
                <div
                  class="ml-auto uppercase text-success-900 text-xs font-semibold mt-2 cursor-pointer"
                  @click="resetFilters"
                >
                  Reset Filters
                </div>
              </div>
              <div class="mt-2 mr-auto pl-4 flex-0">
                {{ totalRecords }} Courses
              </div>
              <div class="flex-1 flex overflow-y-auto">
                <table v-show="!classesDataLoading && classesIsReady">
                  <thead class="bg-white sticky top-0">
                    <tr
                      v-for="headerGroup in table.getHeaderGroups()"
                      :key="headerGroup.id"
                    >
                      <th
                        v-for="header in headerGroup.headers"
                        :key="header.id"
                        :colSpan="header.colSpan"
                        :style="{ width: `${header.getSize()}px` }"
                        class="py-4 pr-3 first:pl-2 text-left text-sm items-center text-gray-900"
                      >
                        <div class="flex items-center">
                          <FlexRender
                            v-if="!header.isPlaceholder"
                            :render="header.column.columnDef.header"
                            :props="header.getContext()"
                          />
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody class="bg-white">
                    <tr
                      v-for="row in table.getRowModel().rows"
                      v-show="!classesDataLoading && classesIsReady"
                      :key="row.id"
                      class="hover:bg-blue-350"
                    >
                      <td
                        v-for="cell in row.getVisibleCells()"
                        :key="cell.column.id"
                        :style="{ width: `${cell.column.getSize()}px` }"
                        class="py-8 px-2 text-sm first:pl-4 first:text-teal-900 capitalize border-t-[1px] border-solid border-t-[#f1f2f4]"
                      >
                        <FlexRender
                          :render="cell.column.columnDef.cell"
                          :props="cell.getContext()"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
                <Spinner
                  v-show="classesDataLoading || !classesIsReady"
                  outer-classes="flex items-center justify-center bg-white h-full relative mx-auto"
                />
              </div>
              <Pagination
                class="mb-4 flex-0"
                :page="pagination.pageIndex"
                :total-of-records="totalRecords"
                :records-per-page="pagination.pageSize"
                @changePage="changePage"
              />
              <div class="flex align-center justify-center h-10 flex-0">
                <BaseButton outlined :on-click="onCancel" class="px-7"
                  >Cancel</BaseButton
                >
                <BaseButton :on-click="addCriteria"
                  >Add This Criteria</BaseButton
                >
              </div>
              <div v-if="v$.$invalid">
                <ErrorMessage
                  :error-classes="'error text-error-900 mt-2 text-center'"
                  :vuelidate-instance="v$"
                />
              </div>
            </section>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  </div>
</template>

<style scoped>
.checkbox-placeholder :deep(.vs__search) {
  font-size: 14px;
}
</style>
